
import { format, parseISO, isValid } from 'date-fns';

/**
 * Format a date string into a human-readable format
 */
export const formatDate = (dateString: string): string => {
  if (!dateString) return '';
  
  try {
    // Ensure we have a string and it's trimmed
    const formattedDateString = String(dateString).trim();
    
    const date = parseISO(formattedDateString);
    if (!isValid(date)) {
      throw new Error('Invalid date');
    }
    return format(date, 'EEEE, MMMM d, yyyy');
  } catch (error) {
    console.error('Error formatting date:', dateString, error);
    
    // Simple fallback
    if (typeof dateString === 'string' && dateString.includes('-')) {
      const parts = dateString.split('-');
      if (parts.length >= 3) {
        const [year, month, day] = parts;
        return `${year}-${month}-${day.substring(0, 2)}`;
      }
    }
    
    return dateString;
  }
};

/**
 * Format a date string into a format with day name (e.g., "Monday, Jan 15")
 */
export const formatDateWithDay = (dateString: string): { day: string; date: string } => {
  if (!dateString) return { day: '', date: '' };
  
  try {
    // Ensure we're working with a string
    const formattedDateString = String(dateString).trim();
    
    // Try to parse the ISO date
    const date = parseISO(formattedDateString);
    
    if (!isValid(date)) {
      throw new Error('Invalid date');
    }
    
    return {
      day: format(date, 'EEEE'),
      date: format(date, 'MMM d')
    };
  } catch (error) {
    console.error("Error formatting date with day:", dateString, error);
    
    // Fallback for YYYY-MM-DD format
    if (typeof dateString === 'string' && dateString.includes('-')) {
      try {
        const [year, month, day] = dateString.split('-').map(Number);
        const date = new Date(year, month - 1, day);
        
        if (isValid(date)) {
          const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
          const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
          
          return { 
            day: dayNames[date.getDay()], 
            date: `${monthNames[month-1]} ${day}` 
          };
        }
      } catch (e) {
        console.error("Fallback date formatting failed:", e);
      }
    }
    
    return { day: 'Day', date: dateString };
  }
};

/**
 * Parse a date string into a Date object
 */
export const parseDate = (dateString: string): Date | null => {
  if (!dateString) return null;
  
  try {
    const date = parseISO(dateString);
    return isValid(date) ? date : null;
  } catch (error) {
    console.error("Error parsing date:", dateString, error);
    return null;
  }
};

/**
 * Format time string to 12-hour format with AM/PM
 */
export const formatTimeTo12Hour = (timeString: string): string => {
  if (!timeString) return '';
  
  try {
    // Handle HH:MM format (24-hour)
    if (/^\d{1,2}:\d{2}$/.test(timeString)) {
      const [hours, minutes] = timeString.split(':').map(Number);
      const period = hours >= 12 ? 'PM' : 'AM';
      const hours12 = hours % 12 || 12; // Convert 0 to 12 for 12 AM
      return `${hours12}:${minutes.toString().padStart(2, '0')} ${period}`;
    }
    
    // If it's already in 12-hour format, return as is
    if (/^\d{1,2}:\d{2}\s?[APap][Mm]$/.test(timeString)) {
      return timeString;
    }
    
    return timeString;
  } catch (error) {
    console.error("Error formatting time:", timeString, error);
    return timeString;
  }
};

